import { Box, Paper, TextField, Typography, Link, Button } from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ToastMessage from "../components/toast-message";
import BackdropLoader from "../components/backdrop-loader";

function Auth() {
    const navigate = useNavigate();

    const [currentState, setCurrentState] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone_number: '',
        password: '',
    });
    const [sentOtp, setSentOtp] = useState("");
    const [otp, setOtp] = useState("");
    const [verifiedOtp, setVerifiedOtp] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [status, setStatus] = useState({ type: 0, message: '' });
    const [loading, setLoading] = useState(false);

    const clearToastMessge = () => setStatus({ type: 0, message: '' });

    const handleSignUpInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const saveToLocalStorage = (id, name, email, phone_number) => {
        const payload = { id, name, email, phone_number };
        localStorage.setItem("user", JSON.stringify(payload));
        navigate("/author-dashboard");
    };

    const login = async () => {
        if (!formData.email || !formData.password) {
            setStatus({ type: 0, message: "please fill in all fields" });
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(
                "https://api.wajls.com/client/v1/verify_login.php",
                { email: formData.email, password: formData.password },
                { headers: { "Content-Type": "application/json" } }
            );

            const result = response.data;
            if (result.success) {
                saveToLocalStorage(result.author_id, result.author_name, formData.email, result.phone_number);
            } else {
                setStatus({ type: 0, message: result.message });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const register = async () => {
        if (!formData.name || !formData.email || !formData.phone_number || !formData.password) {
            setStatus({ type: 0, message: "please fill in all fields" });
            return;
        }
        if (formData.password.length < 4) {
            setStatus({ type: 0, message: "password length is too small" });
            return;
        }

        try {
            setLoading(true);

            const response = await axios.post(
                "https://api.wajls.com/client/v1/create_account.php",
                formData,
                { headers: { "Content-Type": "application/json" } }
            );

            console.log(response.data);
            
            const result = response.data;
            if (result.success) {
                saveToLocalStorage(result.author_id, formData.name, formData.email, formData.phone_number);
            } else {
                setStatus({ type: 0, message: result.message });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const sendResetLink = async () => {
        if (!formData.email) {
            setStatus({ type: 0, message: "enter a valid email" });
            return;
        }

        try {
            setLoading(true);
            
            const response = await axios.post("https://api.wajls.com/client/v1/send_otp.php", { email: formData.email });
            setStatus({
                type: response.data.success ? 1 : 0,
                message: response.data.message,
            });

            if (response.data.success) {
                setSentOtp(response.data.otp);
                setCurrentState(3);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const changePassword = async () => {
        if (verifiedOtp) {
            if (!newPassword) {
                setStatus({ type: 0, message: "choose a new password" });
                return;
            }

            try {
                setLoading(true);
                const response = await axios.post("https://api.wajls.com/client/v1/change_password.php", {
                    email: formData.email,
                    password: newPassword,
                });

                setStatus({
                    type: response.data.success ? 1 : 0,
                    message: response.data.message,
                });

                if (response.data.success) {
                    setCurrentState(0);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        } else {
            if (parseInt(otp) !== sentOtp) {
                setStatus({ type: 0, message: "enter correct OTP" });
                return;
            }
            setVerifiedOtp(true);
        }
    };

    const sharedCardStyle = {
        padding: 3,
        width: 380,
        borderRadius: 3,
        margin: 2,
        backgroundColor: "#fff",
        boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease-in-out",
    };

    const sharedButtonStyle = {
        backgroundColor: "#1976d2",
        mt: 2,
        py: 1.2,
        fontWeight: 600,
        textTransform: "none",
        "&:hover": { backgroundColor: "#1565c0" },
    };

    const sharedInputStyle = {
        backgroundColor: "#f9f9f9",
        borderRadius: 1,
        "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: "#1976d2" },
            "&.Mui-focused fieldset": { borderColor: "#1976d2" },
        },
    };

    const renderTitle = (text) => (
        <Typography
            variant="h6"
            sx={{
                fontWeight: 700,
                color: "#1976d2",
                mb: 3,
                textAlign: "center",
            }}
        >
            {text}
        </Typography>
    );

    const renderSignUp = () => (
        <Paper elevation={4} sx={sharedCardStyle}>
            {renderTitle("Create Account")}
            <TextField fullWidth label="Full Name" margin="normal" name="name" value={formData.name} onChange={handleSignUpInputChange} sx={sharedInputStyle} />
            <TextField fullWidth label="Email Address" margin="normal" name="email" value={formData.email} onChange={handleSignUpInputChange} sx={sharedInputStyle} />
            <TextField fullWidth label="Phone Number" margin="normal" name="phone_number" value={formData.phone_number} onChange={handleSignUpInputChange} sx={sharedInputStyle} />
            <TextField fullWidth label="Choose a Password" margin="normal" name="password" type="password" value={formData.password} onChange={handleSignUpInputChange} sx={sharedInputStyle} />

            <Typography align="center" sx={{ mt: 2 }}>
                Already have an account?
                <Link onClick={() => setCurrentState(0)} sx={{ ml: 1, fontWeight: 600, cursor: "pointer" }} underline="hover">
                    Login
                </Link>
            </Typography>

            <Button variant="contained" fullWidth sx={sharedButtonStyle} onClick={register}>
                Sign Up
            </Button>
        </Paper>
    );

    const renderSignIn = () => (
        <Paper elevation={4} sx={sharedCardStyle}>
            {renderTitle("Welcome Back")}
            <TextField fullWidth label="Email Address" margin="normal" name="email" value={formData.email} onChange={handleSignUpInputChange} sx={sharedInputStyle} />
            <TextField fullWidth label="Password" margin="normal" name="password" type="password" value={formData.password} onChange={handleSignUpInputChange} sx={sharedInputStyle} />

            <Typography align="center" sx={{ mt: 2 }}>
                <Link onClick={() => setCurrentState(1)} sx={{ fontWeight: 600, cursor: "pointer" }} underline="hover">
                    Create Account
                </Link>{" "}
                |{" "}
                <Link onClick={() => setCurrentState(2)} sx={{ fontWeight: 600, cursor: "pointer" }} underline="hover">
                    Forgot Password?
                </Link>
            </Typography>

            <Button variant="contained" fullWidth sx={sharedButtonStyle} onClick={login}>
                Sign In
            </Button>
        </Paper>
    );

    const renderPasswordReset = () => (
        <Paper elevation={4} sx={sharedCardStyle}>
            {renderTitle("Reset Password")}
            <TextField fullWidth label="Email Address" margin="normal" name="email" value={formData.email} onChange={handleSignUpInputChange} sx={sharedInputStyle} />

            <Typography align="center" sx={{ mt: 2 }}>
                Got your password?
                <Link onClick={() => setCurrentState(0)} sx={{ ml: 1, fontWeight: 600, cursor: "pointer" }} underline="hover">
                    Login
                </Link>
            </Typography>

            <Button variant="contained" fullWidth sx={sharedButtonStyle} onClick={sendResetLink}>
                Send OTP
            </Button>
        </Paper>
    );

    const renderOtpAndNewPassword = () => (
        <Paper elevation={4} sx={sharedCardStyle}>
            {renderTitle("Reset Password")}
            {!verifiedOtp && <TextField fullWidth label="Enter OTP" margin="normal" name="otp" value={otp} onChange={(e) => setOtp(e.target.value)} sx={sharedInputStyle} />}
            {verifiedOtp && (
                <TextField
                    fullWidth
                    label="Choose a new password"
                    margin="normal"
                    name="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    sx={sharedInputStyle}
                />
            )}

            <Typography align="center" sx={{ mt: 2 }}>
                {!verifiedOtp && (
                    <>
                        <Link onClick={sendResetLink} sx={{ fontWeight: 600, cursor: "pointer" }} underline="hover">
                            Resend OTP
                        </Link>{" "}
                        |{" "}
                    </>
                )}
                <Link onClick={() => setCurrentState(0)} sx={{ fontWeight: 600, cursor: "pointer" }} underline="hover">
                    Back to Login
                </Link>
            </Typography>

            <Button variant="contained" fullWidth sx={sharedButtonStyle} onClick={changePassword}>
                {verifiedOtp ? "Change Password" : "Verify OTP"}
            </Button>
        </Paper>
    );

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
                background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                transition: "all 0.4s ease-in-out",
            }}
        >
            {loading && <BackdropLoader/>} 

            {currentState === 0 && renderSignIn()}
            {currentState === 1 && renderSignUp()}
            {currentState === 2 && renderPasswordReset()}
            {currentState === 3 && renderOtpAndNewPassword()}

            {status.message && <ToastMessage type={status.type} message={status.message} onClose={clearToastMessge} />}
        </Box>
    );
}

export default Auth;
